import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../utils/axios';
import useAuth from '../../hooks/useAuth';
import { isMaster } from '../../utils/perfil';
import Modal, { AreaArquivo, BotaoPrimario, BotaoSecundario, Campo, classesEntrada } from '../../components/gps/Modal';
import { AutoCompleteUnico, buscarPaginado } from '../../components/gps/AutoComplete';

// Mesmo endpoint e filtro de antes (`gerente=-1`), paginado e so na lupa.
const buscarRegionais = buscarPaginado(
  (termo, pagina, linhas) =>
    `regionais/paginado?page=${pagina}&rows=${linhas}&campo=nome&valor=${encodeURIComponent(termo)}` +
    `&direcao=asc&ordenar=nome&gerente=-1`,
  (r) => ({ codigo: r.codigo, rotulo: r.nome })
);

/* ============================================================================
   Botao flutuante de mensagem / suporte - visual novo.

   Substitui Fab/Badge/Dialog/TextField/Autocomplete do MUI. O envio e igual:
   `FormData` com tipo, descricao, mensagem, `regionalId` opcional e `file`
   opcional, para `POST notificacoes/mensagens` com multipart. Nada do fluxo de
   envio foi mexido.

   O QUE MUDA:

   1. O AVISO DE BROADCAST usava `t.palette.warning.lighter`, que NAO existe no
      MUI 9 (`lighter`/`darker` eram extensoes do template) - por isso havia o
      `|| 'transparent'`: na pratica a caixa de aviso "isso vai para TODOS os
      usuarios" aparecia SEM fundo, so com borda. O aviso mais importante do
      formulario era o menos visivel. Agora usa o token `warn`.

   2. O contador no botao segue a mesma regra das notificacoes: zero nao pinta
      bolinha.

   3. O anexo mostra nome E tamanho do arquivo, e da para remover sem fechar o
      formulario. Antes, escolher o arquivo errado obrigava a cancelar tudo.

   4. O seletor de regional virou busca com lista (500 regionais no Autocomplete
      do MUI abria uma lista rolavel sem filtro util). Desde 21/10/2026 e a lupa
      do ModalSelecionar, paginada no servidor: abrir o formulario nao baixa mais
      as 500 regionais.
   ============================================================================ */

type RegionalOption = { codigo: number; nome: string };
const TIPOS_BASE = ['GERAL', 'SUPORTE', 'DÚVIDA', 'SUGESTÃO', 'PROBLEMA'];
const TIPO_BROADCAST = 'ENVIAR_PARA_TODOS';

export default function BotaoMensagemFlutuante() {
  const { enqueueSnackbar } = useSnackbar();
  // Desde 21/10/2026 o botao de mensagem so existe na tela de Tickets.
  const naTickets = useLocation().pathname.replace(/\/+$/, '') === '/dashboard/tickets';
  const { user } = useAuth();
  const masterUser = isMaster(user);
  const tiposDisponiveis = masterUser ? [...TIPOS_BASE, TIPO_BROADCAST] : TIPOS_BASE;

  const [open, setOpen] = useState(false);
  const [naoLidas, setNaoLidas] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const [tipo, setTipo] = useState('GERAL');
  const [descricao, setDescricao] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [regionalSel, setRegionalSel] = useState<RegionalOption | null>(null);
  const [enviando, setEnviando] = useState(false);

  const carregarContador = useCallback(async () => {
    try {
      const res = await axiosInstance.get<{ total: number }>('notificacoes/contador?tipo=MENSAGEM');
      setNaoLidas(res.data?.total || 0);
    } catch (e) {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    carregarContador();
    timer.current = setInterval(carregarContador, 60000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [carregarContador]);

  const abrir = () => {
    setOpen(true);
  };

  const limpar = () => {
    setTipo('GERAL');
    setDescricao('');
    setMensagem('');
    setArquivo(null);
    setRegionalSel(null);
  };

  const enviar = async () => {
    if (!mensagem.trim()) {
      enqueueSnackbar('Digite a mensagem.', { variant: 'warning' });
      return;
    }
    setEnviando(true);
    try {
      const form = new FormData();
      form.append('tipo', tipo);
      form.append('descricao', descricao);
      form.append('mensagem', mensagem);
      if (regionalSel?.codigo) form.append('regionalId', String(regionalSel.codigo));
      if (arquivo) form.append('file', arquivo);
      await axiosInstance.post('notificacoes/mensagens', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      enqueueSnackbar('Mensagem enviada para o suporte.', { variant: 'success' });
      setOpen(false);
      limpar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao enviar mensagem.', { variant: 'error' });
    } finally {
      setEnviando(false);
    }
  };

  if (!naTickets) return null;

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        title="Mensagens / Suporte"
        aria-label="Mensagens e suporte"
        /* 🔴 DEFEITO CORRIGIDO (26/08/2026): este botao TAPAVA o "Próxima" da
           paginacao.

           Ele tinha 56px (h-14) a 24px das bordas, e o rodape de paginacao das
           listas fica exatamente naquele canto. O resultado era um botao clicavel
           por cima de outro botao clicavel: quem tentava avancar de pagina abria o
           formulario de mensagem.

           Agora tem 44px e fica a 16px da borda inferior. Isso nao resolveria
           sozinho - o que tira a sobreposicao de verdade e o `pb-16` que o rodape
           da lista ganhou, reservando a faixa onde este botao mora. Os dois juntos:
           o botao encolhe e o conteudo deixa de passar por baixo dele. */
        className="fixed bottom-4 right-4 z-[1350] grid h-11 w-11 place-items-center rounded-full bg-primary text-[18px] text-primary-foreground shadow-card transition-transform hover:scale-105"
      >
        <span aria-hidden>&#9993;</span>
        {naoLidas > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-[20px] min-w-[20px] place-items-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
            {naoLidas > 99 ? '99+' : naoLidas}
          </span>
        )}
      </button>

      <Modal
        aberto={open}
        titulo="Nova mensagem"
        descricao="Vai para o suporte do GPShub."
        largura="max-w-[560px]"
        onFechar={() => setOpen(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setOpen(false)}>Cancelar</BotaoSecundario>
            <BotaoPrimario onClick={enviar} disabled={enviando}>
              {enviando ? 'Enviando...' : 'Enviar'}
            </BotaoPrimario>
          </>
        }
      >
        <Campo rotulo="Tipo">
          <select
            className={classesEntrada}
            value={tipo}
            onChange={(e) => {
              const novoTipo = e.target.value;
              setTipo(novoTipo);
              if (novoTipo === TIPO_BROADCAST) setRegionalSel(null);
            }}
          >
            {tiposDisponiveis.map((t) => (
              <option key={t} value={t}>
                {t === TIPO_BROADCAST ? 'Enviar para TODOS (broadcast)' : t}
              </option>
            ))}
          </select>
        </Campo>

        {tipo === TIPO_BROADCAST && (
          <div className="rounded-lg border border-warn bg-warn-soft p-2.5 text-[12px]">
            Essa mensagem chegará para <strong>TODOS os usuários ativos</strong>. Restrita a Master.
          </div>
        )}

        <Campo rotulo="Descrição (assunto)">
          <input className={classesEntrada} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
        </Campo>

        <Campo rotulo="Mensagem" obrigatorio>
          <textarea
            className="min-h-[110px] w-full rounded-lg border border-line bg-surface px-3 py-2 text-[12.5px] text-ink outline-none placeholder:text-faint focus:border-primary"
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
          />
        </Campo>

        {/* Regional fica oculta em broadcast: ja vai para todos. */}
        {tipo !== TIPO_BROADCAST && (
          <Campo bloco rotulo="Regional (opcional)" dica="Sem regional a mensagem vai para o suporte.">
            <AutoCompleteUnico
              selecionado={regionalSel ? { codigo: regionalSel.codigo, rotulo: regionalSel.nome } : null}
              aoEscolher={(o) => setRegionalSel(o ? { codigo: Number(o.codigo), nome: o.rotulo } : null)}
              buscar={buscarRegionais}
              titulo="Selecionar regional"
              placeholder="Nome da regional (Enter ou lupa)"
              vazio="Nenhuma regional encontrada."
            />
          </Campo>
        )}

        {/* Arrastar e soltar aqui tambem: quem esta relatando um problema costuma ter
            o print recem-tirado na area de trabalho, e arrastar e o caminho curto.
            O clipe era o emoji U+1F4CE, que cada sistema desenha do seu jeito (e no
            Windows sai colorido, fora do tom do resto) - agora e SVG na cor do tema. */}
        <AreaArquivo
          arquivo={arquivo}
          onArquivo={setArquivo}
          rotulo="Arraste um arquivo ou print aqui, ou clique para escolher"
          dica="Opcional"
        />
      </Modal>
    </>
  );
}
